import { createSelector, createStructuredSelector } from "reselect"
import { fromJS } from "immutable"
import { makeImmutableSliceSelector, createExtraFieldSelector } from "shared/imports/sharedHelperFunctions"
import moment from "moment-timezone"
import { defaultAlignmentValues } from "shared/stakeholder/constants"

export const stakeholderSelector = state => state.stakeholder

export const selectAlignmentTypes = createSelector(
    stakeholderSelector,
    state => state.get("alignmentTypes", fromJS([]))
)

export const selectAlignmentTypesAsEnum = createSelector(
    selectAlignmentTypes,
    alignmentTypes =>
        alignmentTypes
            .map(alignmentType => fromJS({
                id: alignmentType.get("id"),
                key: alignmentType.get("id"),
                label: alignmentType.get("label"),
                order: alignmentType.get("value"),
                value: alignmentType.get("id"),
                icon: alignmentType.get("icon"),
                iconFamily: alignmentType.get("icon_family")
            }))
            .sort((a, b) => b.get("order") - a.get("order"))
)

export const selectChildrenFromProps = (_, props) => props && props.children

/**
* This function finds the currently selected AlignmentType from props.children
* Used in the StakeholderAlignmentCell
* @name selectSelectedAlignmentType
* @function
* @returns {Object} - The selected AlignmentType object in enum form - { value: number, label: string }
*/
export const selectSelectedAlignmentType = createSelector(
    selectAlignmentTypesAsEnum,
    selectChildrenFromProps,
    (alignmentTypes, children) => alignmentTypes.find(alignmentType => alignmentType.get("value") === children)
)

export const selectAlignmentType = createSelector(
    stakeholderSelector,
    state => state.get("alignmentType")
)

export const selectAlignmentTypeIsLoading = createSelector(
    selectAlignmentType,
    alignmentType => alignmentType && alignmentType.get("isLoading")
)

export const selectAlignmentTypeLabel = createSelector(
    selectAlignmentType,
    alignmentType => alignmentType && alignmentType.get("label")
)

export const selectAlignmentTypeByValue = value => createSelector(
    selectAlignmentTypes,
    alignmentTypes => alignmentTypes && alignmentTypes.find(type => type.get("value") === value)
)

export const selectAlignmentTypeIdByValue = value => createSelector(
    selectAlignmentTypeByValue(value),
    alignmentType => alignmentType && alignmentType.get("id")
)

export const selectAlignmentTypeLabelByValue = value => createSelector(
    selectAlignmentTypeByValue(value),
    alignmentType => alignmentType && alignmentType.get("label")
)

export const selectProfileAlignment = createSelector(
    stakeholderSelector,
    state => state.get("profileAlignment")
)

export const selectIssueAlignments = createSelector(
    stakeholderSelector,
    state => state.get("issueAlignments")
)

export const selectIsProfileAlignmentLoading = createSelector(
    selectProfileAlignment,
    state => state.get("isLoading")
)

export const selectIsIssueAlignmentsLoading = createSelector(
    selectIssueAlignments,
    state => state.get("isLoading")
)

export const selectIssueAlignmentIssues = createSelector(
    selectIssueAlignments,
    issueAlignments => issueAlignments.get("issues")
)

export const selectProfileAlignmentAction = createSelector(
    selectProfileAlignment,
    state => state.get("alignmentAction")
)

export const selectProfileAlignmentActionProp = makeImmutableSliceSelector(selectProfileAlignmentAction)

export const selectProfileAlignmentActionTypeId = createSelector(
    selectProfileAlignmentActionProp("alignment_type"),
    alignmentTypeUri => DjangIO.app.userdata.stakeholder.models.AlignmentType.idFromResourceUri(alignmentTypeUri)
)

export const selectUserId = createSelector(
    selectProfileAlignmentActionProp("user"),
    userUri => DjangIO.app.userdata.models.QuorumUser.idFromResourceUri(userUri)
)

export const selectFullName = createSelector(
    createExtraFieldSelector({
        key: "fullname",
        objectSelector: selectProfileAlignmentActionProp,
        defaultVal: fromJS("")
    }),
    fullName => fullName
)

export const selectUsername = createSelector(
    createExtraFieldSelector({
        key: "username",
        objectSelector: selectProfileAlignmentActionProp,
        defaultVal: fromJS("Unknown"),
    }),
    username => username
)

export const selectCreatedBy = createSelector(
    selectUserId,
    selectFullName,
    selectUsername,
    (userId, fullName, username) => {
        if (userId === Userdata.id) {
            return "You"
        } else {
            return fullName.trim().length > 0
                ? fullName.trim()
                : username
        }
    }
)

export const selectCreatedOn = createSelector(
    selectProfileAlignmentActionProp("effective_start_date"),
    effectiveStartDate => (
        effectiveStartDate
            ? moment(effectiveStartDate).format("MMM D, YYYY")
            : ""
    )
)

export const selectIsProfileAlignmentUpdating = createSelector(
    selectProfileAlignment,
    state => Boolean(state.get("currentAlignmentAction"))
)

export const selectUpdatingIssueAlignments = createSelector(
    selectIssueAlignments,
    issueAlignments => issueAlignments.get("currentAlignmentTypes", fromJS({})).keySeq()
)

export const alignmentAdderConnection = createStructuredSelector({
    alignmentTypes: selectAlignmentTypesAsEnum,
    currentAlignmentType: selectSelectedAlignmentType,
    currentAlignmentTypeId: selectProfileAlignmentActionTypeId,
    createdBy: selectCreatedBy,
    createdOn: selectCreatedOn,
    isLoading: selectIsProfileAlignmentLoading,
    isUpdating: selectIsProfileAlignmentUpdating,
})

export const alignmentTypeFormConnection = createStructuredSelector({
    initialValues: createStructuredSelector({
        supporter: selectAlignmentTypeLabelByValue(defaultAlignmentValues.supporter),
        neutral: selectAlignmentTypeLabelByValue(defaultAlignmentValues.neutral),
        detractor: selectAlignmentTypeLabelByValue(defaultAlignmentValues.detractor),
    }),
    supporterId: selectAlignmentTypeIdByValue(defaultAlignmentValues.supporter),
    neutralId: selectAlignmentTypeIdByValue(defaultAlignmentValues.neutral),
    detractorId: selectAlignmentTypeIdByValue(defaultAlignmentValues.detractor),
    isLoading: selectAlignmentTypeIsLoading,
})

export const alignmentTypeSearchConnection = createStructuredSelector({
    alignmentTypes: selectAlignmentTypesAsEnum,
    selectedAlignmentType: selectSelectedAlignmentType
})
